// ===========================================
// Project Detail Page
// ===========================================
// Main project view with tabbed interface for all project data

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { projectAPI } from '../services/api';

// Modal Components
import ServiceModal from '../components/ServiceModal';
import EnvVariableModal from '../components/EnvVariableModal';
import MilestoneModal from '../components/MilestoneModal';

// Tab Components
import {
  PasswordModal,
  OverviewTab,
  IdeaTab,
  EnvironmentTab,
  RoadmapTab,
  StrategyTab
} from '../components/ProjectDetail';

// Drag and drop imports for reorderable tabs
import { 
  DndContext, 
  closestCenter, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { 
  SortableContext, 
  horizontalListSortingStrategy, 
  useSortable, 
  arrayMove 
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Icons
import { 
  ArrowLeft, 
  Trash2, 
  Clock,
  DollarSign,
  Server,
  X
} from 'lucide-react';

import './ProjectDetail.scss';

// ===========================================
// Default tab configuration for project detail view
// ===========================================
// Tab configuration - Overview now includes tech stack
const DEFAULT_TABS = [
  { id: 'overview', label: 'Overview' },  // Includes README + Tech Stack
  { id: 'idea', label: 'Idea' },  // Planning phase workshop
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'strategy', label: 'Strategy' },  // Strategic planning & growth
  { id: 'environment', label: 'Vault' }  // Files + Keys & secrets
];

// ===========================================
// Sortable Tab Component - Draggable tab button
// ===========================================
const SortableTab = ({ tab, isActive, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`project-detail__tab ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={onClick}
    >
      {tab.label}
    </button>
  );
};

// ===========================================
// Main ProjectDetail Component
// ===========================================
const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    currentProject, 
    getProject, 
    loading, 
    error, 
    focusProject, 
    deleteProject,
    permanentDeleteProject,
    setCurrentProject, 
    deleteEnvVariable 
  } = useProjects();
  // Note: Auth context is used by PasswordModal component for password verification

  // ===========================================
  // State Management
  // ===========================================
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedId, setCopiedId] = useState(null);
  
  // Modal visibility states
  // Delete modal with two modes: 'choose' (archive vs permanent) or null
  const [deleteModal, setDeleteModal] = useState({ show: false, mode: null });
  // Password confirmation modal for permanent delete
  const [deletePasswordModal, setDeletePasswordModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showEnvModal, setShowEnvModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showReadmeUpload, setShowReadmeUpload] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Editing state
  const [editingEnvVar, setEditingEnvVar] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  
  // Password modal state
  const [passwordModal, setPasswordModal] = useState({
    show: false,
    action: null,
    envVar: null
  });

  // ===========================================
  // Tab Order State - persisted to localStorage
  // ===========================================
  const [tabs, setTabs] = useState(() => {
    const savedOrder = localStorage.getItem('devpad_tab_order');
    if (savedOrder) {
      try {
        const savedIds = JSON.parse(savedOrder);
        // Map saved IDs to tabs, filtering out any that no longer exist
        const savedTabs = savedIds
          .map(id => DEFAULT_TABS.find(t => t.id === id))
          .filter(Boolean);
        
        // Find any new tabs in DEFAULT_TABS that aren't in saved order
        // This handles cases where we add new tabs (like techstack replacing services)
        const savedTabIds = savedTabs.map(t => t.id);
        const newTabs = DEFAULT_TABS.filter(t => !savedTabIds.includes(t.id));
        
        // If we have new tabs or saved tabs are incomplete, merge them
        if (newTabs.length > 0 || savedTabs.length < DEFAULT_TABS.length) {
          // Return saved tabs plus any new tabs appended
          return [...savedTabs, ...newTabs];
        }
        
        return savedTabs;
      } catch {
        return DEFAULT_TABS;
      }
    }
    return DEFAULT_TABS;
  });

  // ===========================================
  // DnD Sensors Configuration
  // ===========================================
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  // ===========================================
  // Effects
  // ===========================================
  useEffect(() => {
    if (id) {
      getProject(id);
      focusProject(id);
    }
  }, [id, getProject, focusProject]);

  // ===========================================
  // Handlers
  // ===========================================
  
  // Tab drag end handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setTabs((items) => {
        const oldIndex = items.findIndex(t => t.id === active.id);
        const newIndex = items.findIndex(t => t.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        localStorage.setItem(
          'devpad_tab_order', 
          JSON.stringify(newOrder.map(t => t.id))
        );
        
        return newOrder;
      });
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Password modal handlers
  const handlePasswordSuccess = async () => {
    if (passwordModal.action === 'view') {
      // Fetch decrypted values from API
      const envResponse = await projectAPI.getEnvVariables(project._id || project.id);
      const decryptedEnvVars = envResponse.data.data.envVariables;
      
      // Update project state with decrypted values
      setCurrentProject(prev => ({
        ...prev,
        envVariables: prev.envVariables.map(e => {
          const decrypted = decryptedEnvVars.find(d => (d._id || d.id) === (e._id || e.id));
          return decrypted 
            ? { ...e, decryptedValue: decrypted.value, unlocked: true }
            : e;
        })
      }));
    } else if (passwordModal.action === 'edit') {
      setEditingEnvVar(passwordModal.envVar);
      setShowEnvModal(true);
    } else if (passwordModal.action === 'delete') {
      await deleteEnvVariable(project._id || project.id, passwordModal.envVar._id || passwordModal.envVar.id);
    }
  };

  const requestPasswordConfirm = (action, envVar) => {
    setPasswordModal({ show: true, action, envVar });
  };

  // Project handlers
  const handleReadmeExtracted = async () => {
    await getProject(id);
    setShowReadmeUpload(false);
  };

  // Handle project deletion - supports both archive (soft) and permanent (hard) delete
  const handleDelete = async (permanent = false) => {
    setDeleteLoading(true);
    
    // Use permanent delete or archive based on mode
    const result = permanent 
      ? await permanentDeleteProject(project._id || project.id)
      : await deleteProject(project._id || project.id);
    
    setDeleteLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  // Handler for permanent delete after password verification
  const handlePermanentDeleteConfirmed = async () => {
    setDeletePasswordModal(false);
    await handleDelete(true);
  };

  // ===========================================
  // Status Colors Configuration
  // ===========================================
  const statusColors = {
    idea: 'var(--color-status-idea)',
    active: 'var(--color-status-active)',
    paused: 'var(--color-status-paused)',
    completed: 'var(--color-status-completed)',
    archived: 'var(--color-status-archived)'
  };

  // ===========================================
  // Loading and Error States
  // ===========================================
  if (loading) {
    return (
      <div className="project-detail project-detail--loading">
        <div className="spinner"></div>
        <p>Loading project...</p>
      </div>
    );
  }

  if (error || !currentProject) {
    return (
      <div className="project-detail project-detail--error">
        <h2>Project not found</h2>
        <p>{error || 'The project you\'re looking for doesn\'t exist.'}</p>
        <button className="btn-primary" onClick={() => navigate('/dashboard')}>
          Back to Dev Projects
        </button>
      </div>
    );
  }

  const project = currentProject;
  const projectId = project._id || project.id;

  // ===========================================
  // Render Tab Content
  // ===========================================
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab 
            project={project}
            showReadmeUpload={showReadmeUpload}
            setShowReadmeUpload={setShowReadmeUpload}
            onReadmeExtracted={handleReadmeExtracted}
          />
        );
      case 'idea':
        // Planning phase workshop for app ideation
        return <IdeaTab project={project} />;
      case 'environment':
        // Vault tab now includes both files and secrets
        return (
          <EnvironmentTab 
            project={project}
            onAddVariable={() => {
              setEditingEnvVar(null);
              setShowEnvModal(true);
            }}
            onCopy={copyToClipboard}
            copiedId={copiedId}
            onRequestPassword={requestPasswordConfirm}
            onFilesUpdated={() => getProject(projectId)}
          />
        );
      case 'strategy':
        // Strategy tab - project planning, growth, and monetization
        return <StrategyTab project={project} />;
      case 'roadmap':
        return (
          <RoadmapTab 
            project={project}
            onAddMilestone={() => {
              setEditingMilestone(null);  // Clear any existing edit
              setShowMilestoneModal(true);
            }}
            onEditMilestone={(milestone) => {
              setEditingMilestone(milestone);  // Set milestone to edit
              setShowMilestoneModal(true);
            }}
          />
        );
      default:
        return null;
    }
  };

  // ===========================================
  // Main Render
  // ===========================================
  return (
    <div className="project-detail">
      {/* Header */}
      <header className="project-detail__header">
        <button className="project-detail__back" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Back
        </button>
        
        <div className="project-detail__title-row">
          <div className="project-detail__title">
            <h1>{project.name}</h1>
            <span 
              className="project-detail__status"
              style={{ 
                background: `${statusColors[project.status]}20`,
                color: statusColors[project.status]
              }}
            >
              {project.status}
            </span>
          </div>
          
          <div className="project-detail__actions">
            {/* Delete button only - editing is now inline on Overview tab */}
            <button className="btn-secondary btn-secondary--danger" onClick={() => setDeleteModal({ show: true, mode: 'choose' })}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        
        {project.description && (
          <p className="project-detail__description">{project.description}</p>
        )}
        
        {/* Meta info - Stats only (tech stack shown in Overview tab) */}
        <div className="project-detail__meta">
          <div className="project-detail__stats">
            <span>
              <DollarSign size={14} />
              ${project.totalMonthlyCost?.toFixed(2) || '0.00'}/mo
            </span>
            <span>
              <Server size={14} />
              {project.services?.length || 0} services
            </span>
            <span>
              <Clock size={14} />
              Updated {new Date(project.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </header>

      {/* Tabs - Draggable */}
      <div className="project-detail__tabs">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={tabs.map(t => t.id)} 
            strategy={horizontalListSortingStrategy}
          >
            {tabs.map(tab => (
              <SortableTab
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Tab Content */}
      <div className="project-detail__content">
        {renderTabContent()}
      </div>

      {/* Modals */}
      {/* Delete Choice Modal - Archive or Permanent Delete */}
      {deleteModal.show && deleteModal.mode === 'choose' && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, mode: null })}>
          <div className="modal confirm-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal__header">
              <h2>Delete Project</h2>
              <button className="modal__close" onClick={() => setDeleteModal({ show: false, mode: null })}>
                <X size={20} />
              </button>
            </div>
            
            {/* Icon and Message */}
            <div className="confirm-dialog__content">
              <div 
                className="confirm-dialog__icon"
                style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)' }}
              >
                <Trash2 size={24} />
              </div>
              <p className="confirm-dialog__message">
                What would you like to do with "{project.name}"?
              </p>
            </div>
            
            {/* Action Buttons - Stacked vertically */}
            <div className="confirm-dialog__actions" style={{ flexDirection: 'column', gap: '0.75rem' }}>
              {/* Archive Option - Soft delete, can restore */}
              <button 
                className="btn-secondary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => {
                  setDeleteModal({ show: false, mode: null });
                  handleDelete(false);
                }}
                disabled={deleteLoading}
              >
                Archive Project
                <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '0.5rem' }}>
                  (can restore later)
                </span>
              </button>
              
              {/* Permanent Delete Option - Requires password confirmation */}
              <button 
                className="btn-danger"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => {
                  setDeleteModal({ show: false, mode: null });
                  setDeletePasswordModal(true);
                }}
                disabled={deleteLoading}
              >
                Delete Permanently
                <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '0.5rem' }}>
                  (requires password)
                </span>
              </button>
              
              {/* Cancel Button */}
              <button 
                className="btn-secondary"
                style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
                onClick={() => setDeleteModal({ show: false, mode: null })}
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal for Permanent Delete - Reuses existing PasswordModal component */}
      {deletePasswordModal && (
        <PasswordModal
          action="delete-project"
          envVar={{ key: project.name }}
          onSuccess={handlePermanentDeleteConfirmed}
          onClose={() => setDeletePasswordModal(false)}
        />
      )}

      {showServiceModal && (
        <ServiceModal
          projectId={projectId}
          onClose={() => setShowServiceModal(false)}
          onSaved={() => {
            setShowServiceModal(false);
            getProject(projectId);
          }}
        />
      )}

      {showEnvModal && (
        <EnvVariableModal
          projectId={projectId}
          envVar={editingEnvVar}
          onClose={() => {
            setShowEnvModal(false);
            setEditingEnvVar(null);
          }}
          onSaved={() => {
            setShowEnvModal(false);
            setEditingEnvVar(null);
            getProject(projectId);
          }}
        />
      )}

      {showMilestoneModal && (
        <MilestoneModal
          projectId={projectId}
          milestone={editingMilestone}
          onClose={() => {
            setShowMilestoneModal(false);
            setEditingMilestone(null);
          }}
          onSaved={() => {
            setShowMilestoneModal(false);
            setEditingMilestone(null);
            getProject(projectId);
          }}
        />
      )}

      {passwordModal.show && (
        <PasswordModal
          action={passwordModal.action}
          envVar={passwordModal.envVar}
          onSuccess={handlePasswordSuccess}
          onClose={() => setPasswordModal({ show: false, action: null, envVar: null })}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
